import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import Navbar from "../../components/common/Navbar";
import "../../styles/common/roots.css";
import "../../styles/lessonPage/LessonsPage.css";
import config from "../../config/config";
import easyLessonsData from "../../data/easyLessons.json";
import mediumLessonsData from "../../data/mediumLessons.json";
import hardLessonsData from "../../data/hardLessons.json";
import LessonsCarousel from "../../components/lessonPage/LessonsCarousel";

// Preloader component
const Preloader = () => (
  <div className="preloader m-5">
    <div className="spinner m-5"></div>
  </div>
);

const LessonsPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredLessons, setFilteredLessons] = useState([]);
  const [completedLessons, setCompletedLessons] = useState([]);
  const [inProgressLessons, setInProgressLessons] = useState([]);
  const [availableLessons, setAvailableLessons] = useState([]);
  const [loading, setLoading] = useState(true); // Track loading state

  useEffect(() => {
    window.scrollTo(0, 0);

    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUser(decoded); // triggers re-render & second effect
      } catch (error) {
        console.error("Invalid token:", error);
        setLoading(false); // invalid token = stop loading
      }
    } else {
      setLoading(false); // no token = stop loading
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    // 🔹 Fetch User Lessons
    const fetchUserLessons = async () => {
      try {
        const response = await fetch(
          `${config.API_URL}/api/lesson/user-lessons/${user.id}`
        );
        const data = await response.json();

        if (response.ok) {
          const completed = [];
          const inProgress = [];

          data.lessons.forEach((lesson) => {
            if (lesson.status === "completed") {
              completed.push(lesson);
            } else if (lesson.status === "in_progress") {
              inProgress.push(lesson);
            }
          });

          setCompletedLessons(completed);
          setInProgressLessons(inProgress);
        } else {
          console.error("Failed to fetch lessons:", data.message);
        }
      } catch (error) {
        console.error("Error fetching lessons:", error);
      }

      setLoading(false); // Set loading to false after data is fetched
    };

    // 🔹 Fetch All Lessons
    const fetchAllLessons = async () => {
      try {
        const response = await fetch(
          `${config.API_URL}/api/lesson/all-lessons/${user.id}`
        );
        const data = await response.json();

        if (response.ok) {
          setAvailableLessons(data);
        } else {
          console.error("Failed to fetch all lessons:", data.message);
        }
      } catch (error) {
        console.error("Error fetching all lessons:", error);
      }

      setLoading(false); // Set loading to false after data is fetched
    };

    fetchUserLessons();
    fetchAllLessons();
  }, [user]);

  useEffect(() => {
    const searchLessons = (lessons, isUserLoggedIn) => {
      const filteredLessons = lessons.filter((lesson) => {
        const titleMatch = lesson.title
          .toLowerCase()
          .includes(searchQuery.toLowerCase());

        const wpmMatch =
          lesson.wpm && lesson.wpm.toString().includes(searchQuery);

        return titleMatch || wpmMatch;
      });

      if (isUserLoggedIn) {
        return filteredLessons.sort((a, b) => a.title.localeCompare(b.title));
      } else {
        return filteredLessons.sort((a, b) => {
          const wpmA = a.wpm || 0;
          const wpmB = b.wpm || 0;
          return wpmA - wpmB;
        });
      }
    };

    if (!user) {
      const easyResults = searchLessons(easyLessonsData, false);
      const mediumResults = searchLessons(mediumLessonsData, false);
      const hardResults = searchLessons(hardLessonsData, false);

      setFilteredLessons({
        easy: easyResults,
        medium: mediumResults,
        hard: hardResults,
      });
      } else {
      const completedResults = searchLessons(completedLessons, true);
      const inProgressResults = searchLessons(inProgressLessons, true);
      const availableResults = searchLessons(availableLessons, true);

      setFilteredLessons({
        completed: completedResults,
        inProgress: inProgressResults,
        available: availableResults,
      });
    }
  }, [
    searchQuery,
    completedLessons,
    inProgressLessons,
    availableLessons,
    user,
  ]);

  const handleLessonClick = (lesson) => {
    const { groups, title: displayTitle, wpm } = lesson;
    navigate("/test", { state: { groups, displayTitle, wpm } });
  };

  const handleSubLesson = (lesson) => {
    navigate(`/sub-lesson/${lesson.lesson_id}`, {
      state: { lesson, userId: user.id },
    });
  };

  return (
    <div>
      <Navbar />
      <div className="container-fluid containerDesktop mx-lg-5 mt-5">
        <div className="row">
          <div className="col-lg-6">
            <div className="adContainer">
              <div className="lessonAdContent">
                <div className="lessonAdText">
                  Have you explored the latest updates to our Morse code
                  practice tools yet?
                </div>
                <a href="/practice" className="lessonAdButton mt-lg-4">
                  Begin Training
                </a>
              </div>
              <img
                src="lessonpage-reading.png"
                alt="advert-reading"
                className="lessonAdImage"
              />
            </div>
          </div>

          <div className="col-lg-6 mt-lg-0 mt-4">
            <div className="adContainer">
              <div className="lessonAdContent">
                <div className="tutorialAdText">
                  Discover our new tutorial series to help you master Morse code
                  faster!
                </div>
                <a href="/tutorial" className="lessonAdButton mt-lg-4">
                  View Tutorials
                </a>
              </div>
              <img
                src="lessonpage-tutorial.png"
                alt="advert-tutorial"
                className="lessonAdImage"
              />
            </div>
          </div>
        </div>

        <div className="searchContainer mt-3">
          <input
            type="text"
            className="searchBar"
            placeholder="Search Lessons"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <i className="fas fa-search searchIcon"></i>
        </div>
      </div>

      <div className="container-fluid containerDesktop mx-lg-5 mt-2 mb-4">
        {loading ? (
          <Preloader /> // Show preloader while waiting for the backend to load
        ) : !user ? (
          <div>
            {filteredLessons.easy &&
              filteredLessons.easy.length > 0 && (
                <div>
                  <h1 className="lessonsTitle mb-4">Starter Lessons [5-8 wpm]</h1>
                  <LessonsCarousel
                    lessons={filteredLessons.easy || []}
                    onClick={handleLessonClick}
                  />
                </div>
              )}

              {filteredLessons.medium &&
              filteredLessons.medium.length > 0 && (
                <div>
                  <h1 className="lessonsTitle my-4">Operator Lessons [10-15 wpm]</h1>
                  <LessonsCarousel
                    lessons={filteredLessons.medium || []}
                    onClick={handleLessonClick}
                  />
                </div>
              )}
              
              {filteredLessons.hard &&
              filteredLessons.hard.length > 0 && (
                <div>
                  <h1 className="lessonsTitle my-4">Signal Master Lessons [18-25 wpm]</h1>
                  <LessonsCarousel
                    lessons={filteredLessons.hard || []}
                    onClick={handleLessonClick}
                  />
                </div>
              )}
          </div>
        ) : (
          <div>
            {filteredLessons.inProgress &&
              filteredLessons.inProgress.length > 0 && (
                <div>
                  <h1 className="lessonsTitle mb-4">Ongoing Lessons</h1>
                  <LessonsCarousel
                    lessons={filteredLessons.inProgress || []}
                    onClick={handleLessonClick}
                  />
                </div>
              )}

            {filteredLessons.completed &&
              filteredLessons.available.length > 0 && (
                <div>
                  <h1 className="lessonsTitle my-4">Available Lessons</h1>
                  <LessonsCarousel
                    lessons={filteredLessons.available || []}
                    onClick={handleSubLesson}
                  />
                </div>
              )}

            {filteredLessons.completed &&
              filteredLessons.completed.length > 0 && (
                <div>
                  <h1 className="lessonsTitle my-4">Completed Lessons</h1>
                  <LessonsCarousel
                    lessons={filteredLessons.completed || []}
                    onClick={handleLessonClick}
                  />
                </div>
              )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LessonsPage;
